import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Dropdown, Modal, Table, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import type {
  SorterResult,
  TableCurrentDataSource
} from 'antd/lib/table/interface';
import {
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import copy from 'copy-to-clipboard';
import {
  cancelInvoice,
  exportInvoicesCsv,
  fetchInvoicePdf,
  fetchInvoices
} from 'services/api/invoicing';
import { invoicePayUrl } from 'constants/invoicing';
import {
  Invoice,
  InvoiceDocumentType,
  InvoiceListFilters,
  InvoicePaymentMethod,
  InvoiceSortBy,
  InvoiceStatus
} from 'types/api/invoicing';
import { usePermission } from 'hooks';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import Filters from './components/Filters';
import MarkPaidModal from './components/MarkPaidModal';
import NumberingModal from './components/NumberingModal';
import { INVOICE_COLUMNS } from '../invoiceColumns';
import { confirmAndSendInvoice } from '../sendInvoiceWithToast';

const PAGE_LIMIT = 20;

const Invoices: FC = () => {
  const { checkUserPermission } = usePermission();

  /**
   * The three invoicing codes this screen can act on. Money is its own
   * grant: raising an invoice, declaring it paid and voiding it are
   * different jobs, so a role may hold any combination.
   */
  const may = (action: string) =>
    checkUserPermission(PERMISSION_ROUTES.invoicing, action);

  const mayCreate = may(PERMISSION_ACTIONS.create);
  const mayUpdate = may(PERMISSION_ACTIONS.update);
  const mayDelete = may(PERMISSION_ACTIONS.delete);
  const mayPay = may(PERMISSION_ACTIONS.pay);

  const { modal, message } = App.useApp();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<InvoiceListFilters>({});
  const [sort, setSort] = useState<
    Pick<InvoiceListFilters, 'sortBy' | 'sortOrder'>
  >({});

  const [page, setPage] = useState(1);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [markPaid, setMarkPaid] = useState<Invoice | null>(null);
  const [numberingOpen, setNumberingOpen] = useState(false);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchInvoices({
          ...filters,
          ...sort,
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        setInvoices(res.data);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [filters, sort, page, refreshKey]);

  const onApplyFilters = useCallback((next: InvoiceListFilters) => {
    setPage(1);
    setFilters(next);
  }, []);

  const onCopyPayLink = useCallback(
    (url: string) => {
      copy(url);
      message.success('Payment link copied');
    },
    [message]
  );

  // Server-side sort — the BE whitelists sortBy to id/invoiceNumber/createdAt/
  // dueDate/totalAmount/status (camelCase) + sortOrder ASC|DESC.
  const onTableChange = useCallback(
    (
      _pagination: unknown,
      _tableFilters: unknown,
      sorter: SorterResult<Invoice> | SorterResult<Invoice>[],
      extra: TableCurrentDataSource<Invoice> & { action: string }
    ) => {
      if (extra.action !== 'sort') {
        return;
      }

      const s = Array.isArray(sorter) ? sorter[0] : sorter;

      setSort(
        s && s.order
          ? {
              sortBy: s.field as InvoiceSortBy,
              sortOrder: s.order === 'ascend' ? 'ASC' : 'DESC'
            }
          : {}
      );
      setPage(1);
    },
    []
  );

  const onViewPdf = useCallback(async (invoice: Invoice) => {
    try {
      const blob = await fetchInvoicePdf(invoice.publicId);
      setPdfUrl(URL.createObjectURL(blob));
      setPdfOpen(true);
    } catch (err) {
      showErrorMessage(err);
    }
  }, []);

  const closePdf = () => {
    setPdfOpen(false);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }

    setPdfUrl('');
  };

  const onCancel = useCallback(
    (invoice: Invoice) => {
      modal.confirm({
        title: `Cancel invoice ${invoice.invoiceNumber ?? ''}?`,
        okText: 'Cancel invoice',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        onOk: async () => {
          try {
            await cancelInvoice(invoice.publicId);
            reload();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [modal, reload]
  );

  const onExportCsv = useCallback(async () => {
    try {
      const blob = await exportInvoicesCsv(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invoices.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showErrorMessage(err);
    }
  }, [filters]);

  const columns: ColumnProps<Invoice>[] = useMemo(() => {
    const sortCol = (
      field: InvoiceSortBy
    ): Pick<ColumnProps<Invoice>, 'sorter' | 'sortOrder'> => ({
      sorter: true,
      sortOrder:
        // eslint-disable-next-line no-nested-ternary
        sort.sortBy === field
          ? sort.sortOrder === 'ASC'
            ? 'ascend'
            : 'descend'
          : null
    });

    return [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80, ...sortCol('id') },
      {
        title: 'Hashed ID',
        dataIndex: 'publicId',
        key: 'publicId',
        width: 130,
        render: (value: string) => (
          <Typography.Text
            copyable={{ text: value }}
            style={{ fontFamily: 'monospace' }}
          >
            {value.slice(0, 8)}…
          </Typography.Text>
        )
      },
      { ...INVOICE_COLUMNS.invoiceNumber, ...sortCol('invoiceNumber') },
      {
        title: 'Patient',
        dataIndex: 'patientName',
        key: 'patientName',
        ellipsis: true
      },
      { ...INVOICE_COLUMNS.status, ...sortCol('status') },
      INVOICE_COLUMNS.paymentMethod,
      { ...INVOICE_COLUMNS.total, ...sortCol('totalAmount') },
      { ...INVOICE_COLUMNS.due, ...sortCol('dueDate') },
      { ...INVOICE_COLUMNS.created, ...sortCol('createdAt') },
      {
        title: '',
        key: 'actions',
        align: 'right',
        width: 60,
        fixed: 'right',
        render: (_, row) => {
          // A credit note is not payable and cannot be cancelled — it is
          // reversed with another credit note.
          const isCreditNote =
            row.documentType === InvoiceDocumentType.CreditNote;

          const canCancel =
            mayDelete &&
            !isCreditNote &&
            row.status !== InvoiceStatus.Paid &&
            row.status !== InvoiceStatus.Cancelled;

          // Manual mark-paid is allowed on any non-paid, non-cancelled
          // invoice (the BE permits it regardless of payment method), but
          // it is its own grant: saying the money arrived is a different
          // job from voiding the document.
          const canMarkPaid =
            mayPay &&
            !isCreditNote &&
            row.status !== InvoiceStatus.Paid &&
            row.status !== InvoiceStatus.Cancelled;

          // Pay link is meaningful for an issued stripe invoice. Resolved by
          // invoicePayUrl: the Stripe-hosted page when the invoice carries
          // one, else the on-domain page.
          const canCopyPay =
            !isCreditNote &&
            row.status === InvoiceStatus.Issued &&
            row.paymentMethod === InvoicePaymentMethod.Stripe;

          // Send/resend is meaningful once issued or paid (drafts 400).
          const canSend =
            row.status === InvoiceStatus.Issued ||
            row.status === InvoiceStatus.Paid;

          // A DRAFT'S TWO ACTIONS WERE ONLY ON THE DETAIL PAGE. Edit and
          // Issue are the whole point of a draft, and the list is where an
          // operator meets one — so the row offered Mark paid and Cancel
          // for something that had not been finished yet, and nothing to
          // finish it with.
          const canEditDraft = mayUpdate && row.status === InvoiceStatus.Draft;

          const items: MenuProps['items'] = [
            { key: 'view', label: 'View' },
            ...(canEditDraft
              ? [
                  { key: 'edit', label: 'Edit' },
                  { key: 'issue', label: 'Issue' }
                ]
              : []),
            { key: 'pdf', label: 'PDF' },
            ...(canCopyPay ? [{ key: 'copy', label: 'Copy pay link' }] : []),
            ...(canSend ? [{ key: 'send', label: 'Send' }] : []),
            ...(canMarkPaid ? [{ key: 'paid', label: 'Mark paid' }] : []),
            ...(canCancel
              ? [{ key: 'cancel', label: 'Cancel', danger: true }]
              : [])
          ];

          const onMenuClick: MenuProps['onClick'] = ({ key }) => {
            if (key === 'view') {
              navigate(`/invoicing/invoices/${row.publicId}`);
            } else if (key === 'edit') {
              navigate(`/invoicing/invoices/${row.publicId}/edit`);
            } else if (key === 'issue') {
              // Sent to the detail page rather than issued from here: the
              // zero-quantity refusal names the line it means, and the
              // operator needs the lines in front of them to act on it.
              navigate(`/invoicing/invoices/${row.publicId}`);
            } else if (key === 'pdf') {
              onViewPdf(row);
            } else if (key === 'copy') {
              onCopyPayLink(invoicePayUrl(row));
            } else if (key === 'send') {
              confirmAndSendInvoice(String(row.id), {
                modal,
                message,
                invoiceNumber: row.invoiceNumber,
                email: row.patientEmail
              });
            } else if (key === 'paid') {
              setMarkPaid(row);
            } else if (key === 'cancel') {
              onCancel(row);
            }
          };

          return (
            <Dropdown
              menu={{ items, onClick: onMenuClick }}
              trigger={['click']}
            >
              <Button size="small" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }
      }
    ];
    // mayDelete and mayPay belong here: the permission map arrives with
    // /auth/me, so a memo that ignored them would build every row's menu
    // from the empty map it saw on the first render and never rebuild —
    // hiding Mark paid and Cancel from someone who holds both.
  }, [
    navigate,
    onViewPdf,
    onCancel,
    onCopyPayLink,
    modal,
    message,
    sort,
    mayUpdate,
    mayDelete,
    mayPay
  ]);

  return (
    <PageWrapper
      title="Invoices"
      color="white"
      extra={[
        // Raising an invoice is invoicing.create. Hidden rather than
        // disabled: a button that only ever refuses teaches nothing.
        ...(mayCreate
          ? [
              <Button
                key="new"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/invoicing/invoices/create')}
              >
                New invoice
              </Button>
            ]
          : []),
        <Button key="csv" icon={<DownloadOutlined />} onClick={onExportCsv}>
          Export CSV
        </Button>,
        // Shown to every admin for now. The BE still guards the counter
        // endpoints with @Roles('super_admin'), so an ordinary admin can open
        // the modal and read it but gets a 403 on save — the modal surfaces
        // that message rather than failing silently.
        <Button key="numbering" onClick={() => setNumberingOpen(true)}>
          Numbering
        </Button>
      ]}
    >
      <Filters onSubmit={onApplyFilters} />
      <Table
        dataSource={invoices}
        columns={columns}
        loading={loading}
        rowKey="publicId"
        onChange={onTableChange}
        scroll={{ x: 'max-content' }}
        pagination={{
          total,
          current: page,
          pageSize: PAGE_LIMIT,
          onChange: setPage,
          showSizeChanger: false
        }}
      />
      <Modal
        open={pdfOpen}
        onCancel={closePdf}
        footer={null}
        width={900}
        title="Invoice PDF"
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Invoice PDF"
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        )}
      </Modal>
      <MarkPaidModal
        invoice={markPaid}
        onClose={() => setMarkPaid(null)}
        onSuccess={reload}
      />
      <NumberingModal
        open={numberingOpen}
        onClose={() => setNumberingOpen(false)}
      />
    </PageWrapper>
  );
};

export default Invoices;
